import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { User } from '../../../../shared/types/user';

import { ToastSwal } from '../../../../components/components/general/toastSwals/toastSwal';

import {
    asyncPatchtUserPassword,
    asyncGetUserByEmail,
    asyncValidatePassword,
} from '../../../../services/users.service';
import { UsersMessages } from '../../../../shared/messages/usersMessages';
import { SIGNIN } from '../../../../routes/paths';

export const useUpdatePassword = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [user, setUser] = useState<User>();
    const [step, setStep] = useState(0);
    const [showPasswordRestrictionModal, setShowPasswordRestrictionModal] =
        useState(false);

    const navigate = useNavigate();
    const { usersMessages }: any = UsersMessages;

    const formik = useFormik({
        initialValues: {
            email: '',
            password_current: '',
            password: '',
            password_retry: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .required('El correo es obligatorio')
                .email('El formato del correo es incorrecto'),
            password_current: Yup.string().required(
                'La contraseña actual es obligatoria'
            ),
            password: Yup.string()
                .required('La contraseña es obligatoria')
                .min(8, 'La contraseña debe tener al menos 8 caracteres')
                .max(12, 'La contraseña no puede tener más de 12 caracteres')
                .matches(/[A-Z]/, 'Debe contener al menos una letra mayúscula')
                .matches(/[a-z]/, 'Debe contener al menos una letra minúscula')
                .matches(/[0-9]/, 'Debe contener al menos un número')
                .matches(
                    /[!@#$%^&*()_+\-]/,
                    'Debe contener al menos un símbolo (!@#$%^&*()_+-)'
                ),
            password_retry: Yup.string().when(['password'], {
                is: (password: any) => password !== undefined,
                then: (schema) =>
                    schema
                        .required(
                            'La confirmación de la contraseña es obligatoria'
                        )
                        .oneOf(
                            [Yup.ref('password')],
                            'Las contraseñas no coinciden'
                        ),
            }),
        }),
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: console.log,
    });

    useEffect(() => {
        if (step === 0) {
            formik.validateField('email');
        } else if (step === 1) {
            formik.validateField('password_current');
        } else if (step === 2) {
            formik.validateField('password');
            formik.validateField('password_retry');
        }
    }, [formik.values, step]);

    const handleValidateUser = () => {
        formik.validateField('email').then(() => {
            if (!formik.errors.email) {
                setLoading(true);
                asyncGetUserByEmail(formik.values.email).then((resp: any) => {
                    const user = resp && resp?.items;
                    if (user) {
                        setLoading(false);
                        setUser(user);
                        if (!user.password_change) {
                            setShowPasswordRestrictionModal(true);
                        } else {
                            ToastSwal.fire({
                                text: `Usuario verificado correctamente.`,
                                icon: 'success',
                                timer: 5000,
                                position: 'center-end',
                            });
                            setStep(1);
                        }
                    } else {
                        ToastSwal.fire({
                            text: `Lo sentimos, ${usersMessages.USER_NOT_FOUND}. Por favor verifica el correo e intenta de nuevo.`,
                            icon: 'error',
                            timer: 5000,
                            position: 'center-end',
                        });
                        setLoading(false);
                    }
                });
            }
        });
    };

    const handleValidatePassword = () => {
        setLoading(true);
        asyncValidatePassword(
            user?.username,
            formik.values.password_current
        ).then((res: any) => {
            setLoading(false);
            if (res && res?.items) {
                ToastSwal.fire({
                    text: `Contraseña validada correctamente.`,
                    icon: 'success',
                    timer: 5000,
                    position: 'center-end',
                });
                setStep(2);
            } else {
                ToastSwal.fire({
                    text: `Lo sentimos, ${usersMessages.PASSWORD_INCORRECT}. Por favor verifica la contraseña e intenta de nuevo.`,
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            }
        });
    };

    const handleUpdatePassword = () => {
        if (user?.id) {
            setLoading(true);
            asyncPatchtUserPassword(user?.id, formik.values).then(
                (body: any) => {
                    setLoading(false);
                    if (body && body.items) {
                        ToastSwal.fire({
                            text: usersMessages.MESSAGE_EDIT,
                            icon: 'success',
                            timer: 5000,
                            position: 'center-end',
                        });
                        navigate(SIGNIN);
                    } else {
                        ToastSwal.fire({
                            text: `Lo sentimos, ${
                                usersMessages[body.message]
                            }, Por favor intenta de nuevo.`,
                            icon: 'error',
                            timer: 5000,
                            position: 'center-end',
                        });
                    }
                }
            );
        } else {
            ToastSwal.fire({
                text: usersMessages.USER_NOT_FOUND,
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
        }
    };

    return {
        formik,
        loading,
        user,
        step,
        showPasswordRestrictionModal,
        setShowPasswordRestrictionModal,
        handleValidateUser,
        handleValidatePassword,
        handleUpdatePassword,
    };
};
